import {
    Alert,
    Button,
    CheckBox,
    ColorPickerInput,
    Form,
    InputControl,
    InputLabel,
    Loading,
    Picker,
    RequiredText,
    StyleFunction,
    Surface,
    TextInput,
    useThemedStyle,
} from "@venuepos/react-common";
import {
    usePrintersQuery,
    useVatsQuery,
    useVirtualPrintersQuery,
} from "graphql-sdk";
import React from "react";
import { useTranslation } from "locales";
import { View } from "react-native";
import { Divider } from "react-native-paper";
import Select from "react-select";

import type { IProductGroupInput } from "lib";
import {
    selectComponents,
    selectStyles,
} from "../../../components/multi-picker";

export function ProductGroupForm(props: {
    form: Form<IProductGroupInput>;
    onSubmit: () => void;
    submitButton: [string, string];
}) {
    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);

    const [{ values, errors }, { setValue, handleSubmit }] = props.form;
    const { data: vatsData, loading: vatsLoading } = useVatsQuery();
    const { data: printersData, loading: printersLoading } = usePrintersQuery({
        variables: {
            pagination: {
                pageSize: 999999,
                sort: "name",
                sortDirection: "ASC",
            },
        },
    });

    const { data: virtualPrintersData, loading: virtualPrintersLoading } =
        useVirtualPrintersQuery({
            variables: {
                pagination: {
                    pageSize: 999999,
                    sort: "name",
                    sortDirection: "ASC",
                },
            },
        });

    if (vatsLoading || printersLoading || virtualPrintersLoading) {
        return <Loading />;
    }

    if (!values || !vatsData || !printersData || !virtualPrintersData) {
        return null;
    }

    const printerOptions = [
        {
            label: t(
                "backoffice.product_form.printer.do_not_print",
                "Do not print"
            ),
            options: [
                {
                    value: "",
                    label: t(
                        "backoffice.product_form.printer.do_not_print",
                        "Do not print"
                    ),
                },
            ],
        },
        {
            label: t("backoffice.product_form.printer.printers", "Printers"),
            options: printersData.printers.data
                .filter(itr => itr.class === "Generic")
                .filter(itr => itr.type !== "INTERNAL")
                .map(printer => ({
                    value: printer.id,
                    label: printer.name,
                })),
        },
        {
            label: t(
                "backoffice.product_form.printer.virtual_printers",
                "Virtual printers"
            ),
            options: virtualPrintersData.virtualPrinters.data.map(itr => ({
                label: itr.name,
                value: itr.id,
            })),
        },
    ];

    const defaultPrinterValue = () => {
        let doNotPrint = printerOptions[0];
        const printers = printerOptions[1]?.options?.find(
            itr => itr.value === values.printerId
        );
        const virtualPrinters = printerOptions[2]?.options?.find(
            itr => itr.value === values.printerId
        );
        return printers ?? virtualPrinters ?? doNotPrint;
    };

    return (
        <View style={styles.container} testID="productGroup">
            {values.isExternal && (
                <Alert type="info" style={styles.readonlyContainer}>
                    {t(
                        "backoffice.common.readonly_product_group",
                        "This product group is controlled by an external system via the API and most of the properties can only be changed from there."
                    )}
                </Alert>
            )}

            <Surface>
                <InputControl
                    error={errors.name}
                    description={
                        values.isExternal
                            ? t(
                                  "backoffice.common.readonly_value",
                                  "This value is controlled by an external system via the API and can only be changed from here."
                              )
                            : null
                    }
                >
                    <TextInput
                        label={t("common.name", "Name")}
                        placeholder={t(
                            "backoffice.common.enter_name",
                            "Enter name"
                        )}
                        value={values.name}
                        onChangeText={text => setValue("name", text)}
                        disabled={values.isExternal}
                        testID="name"
                        required={true}
                    />
                </InputControl>
                <InputControl
                    error={errors.externalId}
                    description={
                        values.isExternal
                            ? `${t(
                                  "backoffice.product_group_form.external_id_description",
                                  "Number or ID that is used by another system. Used for reference."
                              )} ${t(
                                  "backoffice.common.readonly_value",
                                  "This value is controlled by an external system via the API and can only be changed from here."
                              )}`
                            : t(
                                  "backoffice.product_group_form.external_id_description",
                                  "Number or ID that is used by another system. Used for reference."
                              )
                    }
                >
                    <TextInput
                        label={t(
                            "backoffice.product_group_form.external_id",
                            "Number"
                        )}
                        placeholder={t(
                            "backoffice.product_group_form.enter_external_id",
                            "Enter number"
                        )}
                        value={values.externalId || ""}
                        onChangeText={text =>
                            setValue("externalId", text || null)
                        }
                        disabled={values.isExternal}
                        testID="externalId"
                    />
                </InputControl>
                <InputControl
                    error={errors.vatId}
                    description={
                        values.isExternal
                            ? t(
                                  "backoffice.common.readonly_value",
                                  "This value is controlled by an external system via the API and can only be changed from here."
                              )
                            : null
                    }
                >
                    {vatsData && vatsData.vats.data ? (
                        <Picker
                            label={t(
                                "backoffice.product_group_form.vat",
                                "VAT percentage"
                            )}
                            onValueChange={value => {
                                setValue("vatId", value);
                            }}
                            selectedValue={values.vatId || undefined}
                            disabled={values.isExternal}
                            testID="vat"
                            required={true}
                        >
                            <Picker.Item
                                label={t(
                                    "backoffice.account.choose_vat",
                                    "Choose VAT"
                                )}
                                value=""
                            />
                            {vatsData!.vats.data.map(vat => (
                                <Picker.Item
                                    key={vat.id}
                                    value={vat.id}
                                    label={vat.name}
                                    testID={"vat" + vat.name}
                                />
                            ))}
                        </Picker>
                    ) : null}
                </InputControl>
                <InputControl
                    error={errors.excludeFromDiscounts}
                    description={
                        values.isExternal
                            ? t(
                                  "backoffice.common.readonly_value",
                                  "This value is controlled by an external system via the API and can only be changed from here."
                              )
                            : null
                    }
                >
                    <CheckBox
                        label={t(
                            "backoffice.product_group_form.exclude_from_discounts",
                            "Exclude from discounts"
                        )}
                        value={values.excludeFromDiscounts}
                        onValueChange={value =>
                            setValue("excludeFromDiscounts", value)
                        }
                        disabled={values.isExternal}
                        testID="excludeFromDiscounts"
                    />
                </InputControl>

                {values.isExternal ? <Divider style={styles.divider} /> : null}

                <InputControl
                    error={errors.color}
                    description={t(
                        "backoffice.product_group_form.color_description",
                        "Write a color code or select the color for products from this product group on a product layout. The color can also be changed from an external system."
                    )}
                >
                    <ColorPickerInput
                        label={t(
                            "backoffice.product_group_form.color",
                            "Color"
                        )}
                        defaultColor={values.color}
                        onChangeColor={newColor => {
                            setValue("color", newColor ?? "");
                        }}
                        testID="color"
                    />
                </InputControl>
                <InputControl error={errors.printerId || undefined}>
                    <InputLabel>
                        {t(
                            "backoffice.product_form.printer.do_print",
                            "Send to printer for order handling"
                        )}
                    </InputLabel>
                    {printersData && printersData.printers.data ? (
                        <Select
                            // This must be set to avoid having the dropdown hidden by other inputs
                            menuPortalTarget={document.body}
                            isSearchable={false}
                            defaultValue={defaultPrinterValue}
                            options={printerOptions}
                            onChange={itm => {
                                // Typesystem does not think that values exist on item
                                setValue("printerId", (itm as any).value);
                            }}
                            styles={selectStyles}
                            components={selectComponents}
                        />
                    ) : null}
                </InputControl>

                <Button onPress={handleSubmit(props.onSubmit)} testID="save">
                    {t(props.submitButton[0], props.submitButton[1])}
                </Button>

                <RequiredText />
            </Surface>
        </View>
    );
}

const styleFunc: StyleFunction = theme => ({
    container: {
        width: "100%",
        maxWidth: theme.dimensions.maxFormWidth,
    },
    divider: {
        marginBottom: theme.spacingScale * 2,
    },

    readonlyContainer: {
        marginBottom: theme.spacingScale * 2,
    },
});
